/** Financing & suppliers: the lenders, lease providers and parts distributors the company works with. The installer
 * chooses: each partner is switched on here (after reading its fee disclosure), one default per kind, and nothing is
 * ranked by fee. Homeowners see "Apply for financing with <Partner>" with the disclosure; "Order parts" in the bill
 * of materials uses the distributors switched on here. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Landmark, Package, Scale, Star } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { usePartnersEnabled } from "../api/queries";
import { Alert, Badge, Button, Card, Empty, Field, Modal, PageHead, Segmented, Spinner, Switch, TextInput, cx, useToast } from "../components/ui";
import { DirIcon } from "../i18n/mirror";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import en from "../i18n/locales/en/rules.json";
import { countryName } from "../lib/regions";

type Item = S["OrganizationPartner"];
type Partner = S["Partner"];
type Kind = S["PartnerKind"];

/** Each kind's title and description (message keys). */
export const KIND_KEYS: Record<Kind, { title: MessageKey; description: MessageKey }> = {
  finance: { title: "rules.partners.kind.finance.title", description: "rules.partners.kind.finance.description" },
  lease: { title: "rules.partners.kind.lease.title", description: "rules.partners.kind.lease.description" },
  distributor: { title: "rules.partners.kind.distributor.title", description: "rules.partners.kind.distributor.description" },
};
/** English titles for pages not yet translated (admin › Partners): use KIND_KEYS with t() instead. */
export const KIND_TITLES: Record<Kind, { title: string; description: string }> = Object.fromEntries(
  Object.entries(KIND_KEYS).map(([kind, keys]) => [kind, { title: en[keys.title as keyof typeof en] as string, description: en[keys.description as keyof typeof en] as string }]),
) as Record<Kind, { title: string; description: string }>;
const KINDS: Kind[] = ["finance", "lease", "distributor"];

/** The disclosure as homeowners read it (company and partner names filled in). */
export function fillDisclosure(partner: Partner, company: string): string {
  return partner.disclosure.split("{company}").join(company).split("{partner}").join(partner.name);
}

function host(url: string | null | undefined): string {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, "") : "";
  } catch {
    return url ?? "";
  }
}

function EnableModal({ item, company, onClose, onConfirm, busy }: { item: Item; company: string; onClose: () => void; onConfirm: () => void; busy: boolean }) {
  const p = item.partner;
  const [read, setRead] = useState(false);
  const t = useT();
  return (
    <Modal
      title={t("rules.partners.enable.title", { partner: p.name })}
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button onClick={onClose}>{t("rules.action.cancel")}</Button>
          <Button variant="primary" disabled={!read} loading={busy} onClick={onConfirm}>
            {t("rules.partners.enable.confirm")}
          </Button>
        </>
      }
    >
      <div className="stack">
        {p.kind !== "distributor" && (
          <Field label={t("rules.partners.enable.disclosure")}>
            <blockquote className="partner-disclosure">{fillDisclosure(p, company)}</blockquote>
          </Field>
        )}
        <Field label={t("rules.partners.fees")}>
          <p className="small" style={{ margin: 0 }}>{p.referral_fee ?? t("rules.partners.enable.noFee")}</p>
        </Field>
        {p.disclosure_basis && (
          <Field label={p.disclosure_required ? t("rules.partners.requiredByLaw") : t("rules.partners.enable.why")}>
            <p className="small muted" style={{ margin: 0 }}>{p.disclosure_basis}</p>
          </Field>
        )}
        {p.signup_url && (
          <p className="small" style={{ margin: 0 }}>
            {t("rules.partners.enable.notPartner", { partner: p.name })}{" "}
            <a href={p.signup_url} target="_blank" rel="noopener noreferrer">
              {t("rules.partners.enable.signUp", { host: host(p.signup_url) })} <DirIcon><ExternalLink size={12} /></DirIcon>
            </a>
          </p>
        )}
        <label className="partner-ack">
          <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} />
          <span className="small">{t("rules.partners.enable.acknowledge", { partner: p.name, company })}</span>
        </label>
      </div>
    </Modal>
  );
}

function DealerField({ item, disabled, onSave, saving }: { item: Item; disabled: boolean; onSave: (body: S["OrganizationPartnerUpdate"]) => void; saving: boolean }) {
  const apply = item.partner.apply;
  const field = apply?.dealer_field ?? "none";
  const current = (field === "dealer_id" ? item.dealer_id : item.dealer_link) ?? "";
  const [value, setValue] = useState(current);
  const t = useT();
  useEffect(() => setValue(current), [current]);
  if (!apply || field === "none") return apply?.dealer_help ? <p className="small muted" style={{ margin: 0 }}>{apply.dealer_help}</p> : null;
  const placeholder = field === "dealer_id" ? apply.template.replace("{dealer_id}", "…") : `https://${apply.link_domains?.[0] ?? ""}/…`;
  return (
    <div className="row partner-dealer" style={{ gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
      <Field label={apply.dealer_label ?? t("rules.partners.dealerId")} hint={apply.dealer_help ?? undefined} className="partner-dealer-field">
        <TextInput value={value} dir="ltr" disabled={disabled} placeholder={placeholder} onChange={(e) => setValue(e.target.value)} aria-label={apply.dealer_label ?? t("rules.partners.dealerId")} />
      </Field>
      <Button size="sm" style={{ marginTop: 24 }} disabled={disabled || value.trim() === current} loading={saving} onClick={() => onSave({ [field]: value.trim() || null })}>
        {t("rules.action.save")}
      </Button>
    </div>
  );
}

function PartnerRow({ item, company, canEdit, onUpdate, pending }: { item: Item; company: string; canEdit: boolean; onUpdate: (body: S["OrganizationPartnerUpdate"]) => void; pending: boolean }) {
  const p = item.partner;
  const [asking, setAsking] = useState(false);
  const t = useT();
  const { locale } = useIntlShape();
  const needsSetup = item.enabled && !item.configured;
  return (
    <div className={cx("partner-row", !item.enabled && "off")}>
      <div className="partner-main">
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <b>{p.name}</b>
          {item.is_default && (
            <Badge tone="grad">{t("rules.partners.default")}</Badge>
          )}
          {p.countries.map((c) => (
            <Badge key={c} plain>
              {countryName(c, locale)}
            </Badge>
          ))}
          <Badge plain>{p.integration === "api" ? "API" : t("rules.partners.link")}</Badge>
          {p.api.available && <Badge plain>{t("rules.partners.apiAgreement")}</Badge>}
          {needsSetup && <Badge tone="warn">{t("rules.partners.needsSetup")}</Badge>}
        </div>
        <div className="small muted" style={{ marginTop: 2 }}>
          <a href={p.website} target="_blank" rel="noopener noreferrer" dir="ltr">
            {host(p.website)}
          </a>
          {p.kind === "distributor" && p.order?.note ? <> · {p.order.note}</> : null}
        </div>
        {item.enabled && (
          <div className="stack" style={{ marginTop: 8, gap: 8 }}>
            <DealerField item={item} disabled={!canEdit} saving={pending} onSave={onUpdate} />
            {p.kind !== "distributor" && (
              <div className="partner-disclosure small">
                <Scale size={13} /> <span>{fillDisclosure(p, company)}</span>
                {p.disclosure_required && <Badge tone="info">{t("rules.partners.requiredByLaw")}</Badge>}
              </div>
            )}
            <div className="small muted">{p.referral_fee ? t("rules.partners.feesKnown", { fee: p.referral_fee }) : t("rules.partners.feesUnknown", { partner: p.name })}</div>
            {canEdit && !item.is_default && (
              <div>
                <Button size="sm" variant="ghost" icon={<Star size={13} />} onClick={() => onUpdate({ is_default: true })}>
                  {t("rules.partners.makeDefault")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <Switch
        checked={item.enabled}
        disabled={!canEdit || pending}
        label={<span className="sr-only">{t("rules.partners.switch", { partner: p.name })}</span>}
        onChange={(on) => (on ? setAsking(true) : onUpdate({ enabled: false }))}
      />
      {asking && (
        <EnableModal
          item={item}
          company={company}
          busy={pending}
          onClose={() => setAsking(false)}
          onConfirm={() => {
            onUpdate({ enabled: true, acknowledge_fees: true });
            setAsking(false);
          }}
        />
      )}
    </div>
  );
}

/** Shown when the page is opened while the feature is off for the company (the menu has no item then). */
function NotEnabled() {
  const t = useT();
  return (
    <>
      <PageHead title={t("rules.partners.page.title")} />
      <Card>
        <Empty icon={<Landmark />} title={t("rules.partners.notEnabled.title")}>
          {t("rules.partners.notEnabled.body")}
        </Empty>
      </Card>
    </>
  );
}

export function Partners() {
  const enabled = usePartnersEnabled(useOrgId());
  if (enabled === undefined) return <Spinner />;
  return enabled ? <PartnersSettings /> : <NotEnabled />;
}

function PartnersSettings() {
  const orgId = useOrgId();
  const org = useOrg();
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  const canEdit = org.can("settings_rules", "edit");
  const company = org.orgName;
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const key = ["partners", orgId];
  const partners = useQuery({ queryKey: key, queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/partners", { params: { path: { org_id: orgId } } })) });
  const update = useMutation({
    mutationFn: ({ partner, body }: { partner: string; body: S["OrganizationPartnerUpdate"] }) =>
      unwrap(api.PUT("/v1/orgs/{org_id}/partners/{partner_key}", { params: { path: { org_id: orgId, partner_key: partner } }, body })),
    onSuccess: (data, { partner, body }) => {
      qc.setQueryData(key, data);
      const name = data.items.find((i) => i.partner.key === partner)?.partner.name ?? partner;
      const change = body.enabled === true ? "on" : body.enabled === false ? "off" : body.is_default ? "default" : "saved";
      toast(t("rules.partners.toast", { partner: name, change }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const countries = partners.data?.countries ?? [];
  const shown = useMemo(() => {
    const items = partners.data?.items ?? [];
    if (scope === "all" || !countries.length) return items;
    return items.filter((i) => i.enabled || i.partner.countries.some((c) => countries.includes(c)));
  }, [partners.data, scope, countries]);

  return (
    <>
      <PageHead
        title={t("rules.partners.page.title")}
        description={t("rules.partners.page.description")}
        actions={
          countries.length ? (
            <Segmented
              value={scope}
              onChange={setScope}
              options={[
                { value: "mine", label: intl.formatList(countries.map((c) => countryName(c, intl.locale)), { type: "unit", style: "short" }) },
                { value: "all", label: t("rules.partners.allCountries") },
              ]}
            />
          ) : null
        }
      />
      <div className="stack">
      <Alert tone="info" title={t("rules.partners.fees.title")}>
        {t("rules.partners.fees.body")}
      </Alert>
      {partners.isLoading ? (
        <Spinner />
      ) : partners.error || !partners.data ? (
        <Alert tone="danger">{errorMessage(partners.error)}</Alert>
      ) : (
        KINDS.map((kind) => {
          const items = shown.filter((i) => i.partner.kind === kind);
          const Icon = kind === "distributor" ? Package : Landmark;
          return (
            <Card
              key={kind}
              title={
                <span className="row" style={{ gap: 8 }}>
                  <Icon size={16} /> {t(KIND_KEYS[kind].title)}
                </span>
              }
              description={t(KIND_KEYS[kind].description)}
            >
              {items.length ? (
                items.map((item) => (
                  <PartnerRow
                    key={item.partner.key}
                    item={item}
                    company={company}
                    canEdit={canEdit}
                    pending={update.isPending && update.variables?.partner === item.partner.key}
                    onUpdate={(body) => update.mutate({ partner: item.partner.key, body })}
                  />
                ))
              ) : (
                <p className="small muted" style={{ margin: 0 }}>{t("rules.partners.none")}</p>
              )}
            </Card>
          );
        })
      )}
      </div>
    </>
  );
}
